(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Size lens — "shop your size" (Soft Lens model).

     Picking a size re-sorts matching pieces to the front of the existing
     product grid (CSS `order` on the <li>s) and injects a per-size
     availability strip into each card (no verdict copy). Nothing is hidden,
     nothing is dimmed. Operates on the server-rendered card-product cards, so all
     card features (video, casual/runway toggle, quick-add) are preserved.

     Per-card data comes from data-fc-* attributes emitted by
     sections/main-collection-product-grid.liquid:
       data-fc-all-sizes  — every size value the product offers (uppercased)
       data-fc-buy-sizes  — sizes with a purchasable variant (in stock OR
                            continue-selling/pre-order)
       data-fc-pre-sizes  — purchasable sizes that are out of stock
                            (continue-selling / pre-order)
     Combo values like "M/L" are emitted raw and split here.

     The classify()/RANK layer is intentionally isolated so a future
     "fit profile" mode ("I'm an M and I wear it oversized") can re-rank
     without touching the rendering.
  ------------------------------------------------------------------- */

  // The selected size persists in a cookie so it survives full-page navigation
  // (notably pagination, which reloads the page rather than re-rendering the
  // grid in place). On load the stored size is re-applied; the MutationObserver
  // covers in-page grid re-renders (facets / sort via Section Rendering).
  var COOKIE = 'fc_size';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

  function readCookie() {
    var m = document.cookie.match(/(?:^|;\s*)fc_size=([^;]+)/);
    if (!m) return null;
    var v = decodeURIComponent(m[1]).toUpperCase().trim();
    return v || null;
  }
  function writeCookie(v) {
    document.cookie = COOKIE + '=' + encodeURIComponent(v) +
      '; path=/; max-age=' + COOKIE_MAX_AGE + '; samesite=lax';
  }
  function clearCookie() {
    document.cookie = COOKIE + '=; path=/; max-age=0; samesite=lax';
  }

  // Expand a raw size CSV (may contain combos like "M/L") into a token set,
  // keeping only values on the canonical scale.
  function toSizeSet(csv, scale) {
    var set = {};
    if (!csv) return set;
    csv.split(',').forEach(function (tok) {
      tok = tok.trim().toUpperCase();
      if (!tok) return;
      tok.split('/').forEach(function (p) {
        p = p.trim();
        if (scale.indexOf(p) !== -1) set[p] = true;
      });
    });
    return set;
  }

  var RANK = { exact: 0, both: 1, up: 2, down: 2, oneSize: 3, miss: 4 };

  function SizeLens(bar) {
    this.bar = bar;
    var raw = (bar.getAttribute('data-scale') || 'XS,S,M,L,XL,XXL').toUpperCase();
    this.scale = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    this.labelBase = bar.getAttribute('data-size-lens-label') || 'Your size';
    this.labelActive = bar.getAttribute('data-size-lens-label-active') || 'Shopping by size';
    this.pill = document.querySelector('[data-size-lens-pill]');
    this.size = null;
    this.models = [];

    // Both the bar dropdown and the optional floating pill are "controls" of
    // the same instance: one menu-build, one apply(), one cookie — so the two
    // can never hold different values.
    this.controls = [];
    var roots = this.pill ? [bar, this.pill] : [bar];
    for (var i = 0; i < roots.length; i++) {
      var dd = roots[i].querySelector('[data-size-lens-dd]');
      if (!dd) continue;
      this.controls.push({
        dd: dd,
        menu: roots[i].querySelector('[data-size-lens-menu]'),
        labelText: roots[i].querySelector('[data-size-lens-label-text]')
      });
    }

    this.buildModels();
    if (!this.models.length) { bar.hidden = true; return; }
    bar.hidden = false;

    this.buildMenu();
    this.bindDropdown();

    var stored = readCookie();
    this.apply(stored && this.scale.indexOf(stored) !== -1 ? stored : null);
    this.observe();
    this.watchPill();
  }

  SizeLens.prototype.buildModels = function () {
    var scale = this.scale;
    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-fc-card]'));
    this.models = cards.map(function (li, i) {
      var all = toSizeSet(li.getAttribute('data-fc-all-sizes'), scale);
      return {
        li: li,
        origin: i,
        all: all,
        buy: toSizeSet(li.getAttribute('data-fc-buy-sizes'), scale),
        pre: toSizeSet(li.getAttribute('data-fc-pre-sizes'), scale),
        hasAxis: Object.keys(all).length > 0
      };
    });
  };

  // Sizes present anywhere in the collection — drives which pills to show.
  SizeLens.prototype.presentSizes = function () {
    var present = {}, self = this;
    this.models.forEach(function (m) {
      self.scale.forEach(function (s) { if (m.all[s]) present[s] = true; });
    });
    return this.scale.filter(function (s) { return present[s]; });
  };

  SizeLens.prototype.closeMenus = function () {
    this.controls.forEach(function (c) { c.dd.open = false; });
  };

  SizeLens.prototype.buildMenu = function () {
    var self = this;
    var present = this.presentSizes();
    this.controls.forEach(function (c) {
      if (!c.menu) return;
      c.menu.innerHTML = '';
      var addOption = function (value, label) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'size-lens__option' + (value === '' ? ' size-lens__option--all' : '');
        b.setAttribute('role', 'option');
        b.setAttribute('data-size', value);
        b.textContent = label;
        b.addEventListener('click', function () {
          self.apply(value && self.size === value ? null : (value || null));
          self.closeMenus();
        });
        c.menu.appendChild(b);
      };
      addOption('', 'View all'); // the quick "reset" — styled bold, never muted
      present.forEach(function (s) { addOption(s, s); });
    });
  };

  SizeLens.prototype.bindDropdown = function () {
    var self = this;
    document.addEventListener('click', function (e) {
      self.controls.forEach(function (c) {
        if (c.dd.open && !c.dd.contains(e.target)) c.dd.open = false;
      });
    });
    this.controls.forEach(function (c) {
      c.dd.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') c.dd.open = false;
      });
    });
  };

  // --- classification (kept separate so a fit-intent re-rank can build on it) ---
  SizeLens.prototype.classify = function (m, size) {
    if (!m.hasAxis) return 'oneSize';
    if (m.buy[size]) return 'exact';
    var i = this.scale.indexOf(size);
    var down = this.scale[i - 1], up = this.scale[i + 1];
    var hasDown = down && m.buy[down], hasUp = up && m.buy[up];
    if (hasDown && hasUp) return 'both';
    if (hasUp) return 'up';
    if (hasDown) return 'down';
    return 'miss';
  };

  // Per-size cell state (no verdict copy — the strip alone carries availability):
  //   base: available | preorder | unavailable | notcarried
  //   when it IS the chosen size: available→selected, preorder→selpre, else→selout
  SizeLens.prototype.cellState = function (m, s, isSel) {
    var base = !m.all[s]
      ? 'notcarried'
      : (m.buy[s] ? (m.pre[s] ? 'preorder' : 'available') : 'unavailable');
    if (isSel) {
      if (base === 'available') return 'selected';
      if (base === 'preorder') return 'selpre';
      return 'selout';
    }
    return base === 'notcarried' ? 'unavailable' : base;
  };

  SizeLens.prototype.cellsHTML = function (m, size) {
    var self = this;
    return this.scale.map(function (s) {
      var st = self.cellState(m, s, s === size);
      return '<span class="size-lens-cell is-' + st + '" aria-hidden="true">' +
        '<span class="size-lens-cell__l">' + s + '</span></span>';
    }).join('');
  };

  SizeLens.prototype.hostFor = function (li) {
    // Dawn renders two .card__content blocks: one overlaid on the media
    // (inside .card__inner) and one below it holding the visible title/price.
    // Inject into the latter so the strip sits under title + price — not over
    // the image.
    var blocks = li.querySelectorAll('.card__content');
    var fallback = null;
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      if (!b.querySelector('.card-information') && !b.querySelector('.card__heading')) continue;
      if (!fallback) fallback = b;
      if (!b.closest('.card__inner')) return b; // the content block below the media
    }
    return fallback || blocks[0] || li;
  };

  SizeLens.prototype.apply = function (size) {
    var self = this;
    this.size = (size && this.scale.indexOf(size) !== -1) ? size : null;

    // persist (or clear) so the choice survives pagination / full reloads
    if (this.size) writeCookie(this.size); else clearCookie();

    // dropdown label ("Shop by size" idle / "Shopping by size: M" selected) +
    // active option, mirrored across every control (bar dropdown + floating pill)
    var labelValue = this.size ? (this.labelActive + ': ' + this.size) : this.labelBase;
    this.controls.forEach(function (c) {
      if (c.labelText) c.labelText.textContent = labelValue;
      if (!c.menu) return;
      Array.prototype.forEach.call(c.menu.children, function (b) {
        var on = (b.getAttribute('data-size') || '') === (self.size || '');
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    });

    this.models.forEach(function (m) {
      var old = m.li.querySelector('[data-fc-strip]');
      if (old) old.parentNode.removeChild(old);

      if (!self.size) { m.li.style.order = ''; return; }

      var kind = self.classify(m, self.size);
      m.li.style.order = RANK[kind];

      var el = document.createElement('div');
      el.setAttribute('data-fc-strip', '');
      if (kind === 'oneSize') {
        el.className = 'size-lens-onesize';
        el.textContent = 'One size';
      } else {
        el.className = 'size-lens-strip';
        el.innerHTML = self.cellsHTML(m, self.size);
      }
      self.hostFor(m.li).appendChild(el);
    });
  };

  // Re-apply after Dawn re-renders the grid (filter/sort via Section Rendering
  // swaps #ProductGridContainer's children). The bar lives outside the
  // container, so its handlers survive; we just rebuild card models and re-apply.
  SizeLens.prototype.observe = function () {
    var container = document.getElementById('ProductGridContainer');
    if (!container || !window.MutationObserver) return;
    var self = this, t = null;
    new MutationObserver(function () {
      if (t) return;
      t = setTimeout(function () {
        t = null;
        self.buildModels();
        // Pills stay stable (built once at init from the full collection); just
        // re-annotate / re-sort whatever cards are now rendered. apply() handles
        // an empty grid gracefully — summary resets to "0 in your size" rather
        // than leaving a stale count when filters return nothing.
        self.apply(self.size);
      }, 120);
    }).observe(container, { childList: true }); // non-subtree: ignores our own strip injections
  };

  /* Floating pill visibility. Base gate: the pill only ever shows once the
     FILTER & SORT bar has scrolled out of view above the viewport — while the
     bar is on screen the bar dropdown is the control. On top of that gate the
     scroll-behavior setting applies (mirroring the casual toggle's options):
       always                — visible whenever past the bar
       reveal_on_scroll_down — visible past the bar only while scrolling down
       reveal_on_scroll_up   — visible past the bar only when scrolling back up */
  SizeLens.prototype.watchPill = function () {
    var pill = this.pill;
    if (!pill) return;
    var barEl = document.querySelector('.fc-collection-bar') || this.bar;
    var mode = pill.getAttribute('data-scroll-behavior') || 'always';
    var DEADZONE = 2;
    var self = this;
    var lastY = window.scrollY || window.pageYOffset || 0;
    var ticking = false;

    pill.hidden = false; // root unhidden; visibility runs via .is-visible

    function update() {
      var y = window.scrollY || window.pageYOffset || 0;
      var delta = y - lastY;
      lastY = y;
      var pastBar = barEl.getBoundingClientRect().bottom < 0;

      var visible = pill.classList.contains('is-visible');
      if (!pastBar) {
        visible = false;
      } else if (mode === 'reveal_on_scroll_up') {
        if (delta < -DEADZONE) visible = true;
        else if (delta > DEADZONE) visible = false;
      } else if (mode === 'reveal_on_scroll_down') {
        if (delta > DEADZONE) visible = true;
        else if (delta < -DEADZONE) visible = false;
      } else {
        visible = true;
      }

      if (!visible && pill.classList.contains('is-visible')) self.closeMenus();
      pill.classList.toggle('is-visible', visible);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  };

  function init() {
    var bar = document.querySelector('[data-size-lens]');
    if (bar && !bar.__sizeLens) bar.__sizeLens = new SizeLens(bar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
